"use client";

import React, { createContext, useState, useContext, useEffect, useMemo } from "react";

import api from "../api/api";

const LanguageContext = createContext();

export const LanguageProvider = ({ children}) => {
  const [language, setLanguage] = useState("en");
  const [translations, setTranslations] = useState({});
  const [isAutoDetecting, setIsAutoDetecting] = useState(false);
  const [detectionComplete, setDetectionComplete] = useState(false);

  const fetchTranslations = async (lang = "en") => {
    try {
      // 1️⃣ Try detected language first
      const res = await api.get(`/translations?lang=${lang}`);

      if (res?.data && Object.keys(res.data).length > 0) {
        setTranslations(res.data);
        return;
      }

      // 2️⃣ Empty response → fallback
      console.warn(`No translations found for ${lang}, falling back to EN`);

      const fallbackRes = await api.get(`/translations?lang=en`);
      setTranslations(fallbackRes.data || {});

    } catch (error) {
      console.error(`Translation load failed for ${lang}`, error);

      // 3️⃣ Error → fallback to English
      try {
        const fallbackRes = await api.get(`/translations?lang=en`);
        setTranslations(fallbackRes.data || {});
      } catch (fallbackError) {
        console.error("English fallback failed", fallbackError);
        setTranslations({});
      }
    }
  };

  // Detect language from IP
  const detectLanguageFromIP = async () => {
    try {
      setIsAutoDetecting(true);
      const res = await api.get('/detect-language-from-ip');

      if (res.data && res.data.language) {
        const detectedLang = res.data.language;

        // Store in localStorage to avoid repeated detection
        localStorage.setItem('detected_language', detectedLang);
        localStorage.setItem('language_detection_done', 'true');

        setLanguage(detectedLang);
        return detectedLang;
      }
    } catch (error) {
      console.error("Error detecting language from IP:", error);
    } finally {
      setIsAutoDetecting(false);
      setDetectionComplete(true);
    }

    return 'en'; // Fallback
  };

  // Initialize language on mount
  useEffect(() => {
    const initializeLanguage = async () => {
      // Check if user has manually selected a language before
      const userSelectedLang = localStorage.getItem('user_selected_language');

      if (userSelectedLang) {
        // User has manually selected a language, use that
        setLanguage(userSelectedLang);
        setDetectionComplete(true);
        return;
      }

      // Check if we've already detected language
      const detectionDone = localStorage.getItem('language_detection_done');
      const detectedLang = localStorage.getItem('detected_language');

      if (detectionDone && detectedLang) {
        // Use previously detected language
        setLanguage(detectedLang);
        setDetectionComplete(true);
      } else {
        // Detect language from IP (first time visit)
        await detectLanguageFromIP();
      }
    };

    initializeLanguage();
  }, []);

  // Fetch translations when language changes
  useEffect(() => {
    if (language) {
      fetchTranslations(language);
    }
  }, [language]);

  // Custom setLanguage that marks it as user-selected
  const changeLanguage = (newLang) => {
    setLanguage(newLang);
    localStorage.setItem('user_selected_language', newLang);
    localStorage.setItem('detected_language', newLang);
  };

  const t = (key) => {
    return translations[key] || key; // fallback if not translated
  };

  const value = useMemo(
    () => ({
      language,
      setLanguage: changeLanguage, // Use custom function
      t,
      translations,
      isAutoDetecting,
      detectionComplete,
    }),
    [language, translations, isAutoDetecting, detectionComplete]
  );

  return (

    <LanguageContext.Provider value={value}>

      {children}

    </LanguageContext.Provider>

  );

};



export const useLanguage = () => useContext(LanguageContext);

